import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import images from '../../constants/images';
// import { useNavigate } from "react-router-dom";
import '../../assets/styles/global.css';
import styles from './Header.module.css';
import './shiny.button.css';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarDropdownOpen, setSidebarDropdownOpen] = useState(false); // Sidebar dropdown state
  const currentRoute = window.location.pathname;

  // const navigate = useNavigate();

  // function contact() {
  //   navigate("contactUs");
  // }

  const toggleDropdown = (state: boolean) => {
    setIsDropdownOpen(state);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleSidebarDropdown = () => {
    setSidebarDropdownOpen(!isSidebarDropdownOpen);
  };

  const { t } = useTranslation('global');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 455) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-screen h-28 2xl:h-32 flex flex-wrap items-center justify-center z-50 transition-colors duration-300 font ${
        styles.header
      } ${
        isScrolled
          ? 'bg-white text-black shadow-md'
          : 'bg-black bg-opacity-5 text-white'
      }`}
    >
      <div
        className={`container mx-auto px-8 xl:px-14 2xl:px-8 py-4 flex flex-wrap justify-between items-center ${styles.content}`}
      >
        {/* Logo */}
        <div className="flex items-center space-x-2 sm:ml-[-4px] md:ml-[-6px] lg:ml-[-10px] xl:ml-[-12px] 2xl:ml-[-24px]">
          <a href="/">
            <img
              src={isScrolled ? images.logo_white_bg : images.logo_transparant}
              alt="Elyunax Logo"
              className={`h-10 sm:h-12 lg:h-14 2xl:h-16 w-auto max-w-[155px] 2xl:max-w-[200px] ${styles.logo}`}
            />
          </a>
        </div>

        {/* Hamburger Icon for Mobile */}
        <div className="lg:hidden">
          <button
            className="text-3xl focus:outline-none"
            onClick={toggleSidebar}
          >
            <img
              src={images.hamburger_icon}
              alt="Menu"
              className={`w-8 h-8 ${isScrolled ? 'filter invert' : ''}`}
            />
          </button>
        </div>

        {/* Desktop Navigation Menu */}
        <nav
          className={`hidden lg:flex items-center space-x-8 2xl:space-x-10 text-lg h-[80px]`}
          style={{ fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}
        >
          {/* Company Dropdown */}
          <div className="relative" onMouseLeave={() => toggleDropdown(false)}>
            <button
              className={`h-[80px] ${isScrolled ? 'hover:border-b-4 border-b-transparent pb-1' : 'hover:border-b-4 border-b-transparent pb-1'}  lg:text-base xl:text-lg 2xl:text-xl uppercase tracking-wide flex items-center  ${styles.text}`}
              onMouseEnter={() => toggleDropdown(true)}
            >
              {t('header.company')}{' '}
              <svg
                className={`w-4 h-4 ml-1 transform transition-transform duration-300 ${
                  isDropdownOpen ? 'rotate-180' : 'rotate-0'
                } ${styles.icon}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <ul
                className={`absolute text-center py-3 w-auto mt-[-15px] min-w-[175px] max-w-[90%]  lg:min-w-[150px] xl:min-w-[175px] 2xl:min-w-[190px] bg-white text-black shadow-md right-[-20px] z-50`}
                onMouseLeave={() => toggleDropdown(false)}
              >
                {/* Menu Item */}
                <li className="px-4 py-3 flex justify-center items-center hover:bg-gray-100 mb-2 ">
                  <a
                    href="/about-us"
                    className={`block uppercase tracking-wide lg:text-base xl:text-lg 2xl:text-xl ${
                      currentRoute === '/about-us'
                        ? 'text-red-500'
                        : 'text-[#111111]'
                    }`}
                    style={{ fontWeight: 600 }}
                  >
                    {t('header.about')}
                  </a>
                </li>

                {/* Certificate */}
                <li className="px-4 py-3 flex justify-center items-center hover:bg-gray-100 mb-2">
                  <a
                    href="/certifications"
                    className={`block uppercase tracking-wide lg:text-base xl:text-lg 2xl:text-xl ${
                      currentRoute === '/certifications'
                        ? 'text-red-500'
                        : 'text-[#111111]'
                    }`}
                    style={{ fontWeight: 600 }}
                  >
                    {t('header.certificate')}
                  </a>
                </li>

                {/* Careers */}
                <li className="px-4 py-3 flex justify-center items-center hover:bg-gray-100">
                  <a
                    href="/careers"
                    className={`block uppercase tracking-wide lg:text-base xl:text-lg 2xl:text-xl ${
                      currentRoute === '/careers'
                        ? 'text-red-500'
                        : 'text-[#111111]'
                    }`}
                    style={{ fontWeight: 600 }}
                  >
                    {t('header.careers')}
                  </a>
                </li>

                {/* Social Icons */}
                <div className="flex justify-center space-x-3 px-4 py-3 mt-2  ">
                  <a
                    href="https://www.facebook.com/elyumax"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={images.facebook_gray}
                      alt="Facebook"
                      className="w-6 h-6 hover:scale-125 transition-transform duration-300"
                    />
                  </a>
                  <a
                    href="https://www.instagram.com/elyumax"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={images.instagram_gray}
                      alt="Instagram"
                      className="w-6 h-6 hover:scale-125 transition-transform duration-300"
                    />
                  </a>
                  <a
                    href="https://x.com/elyumax"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={images.twitter_gray}
                      alt="Twitter"
                      className="w-6 h-6 hover:scale-125 transition-transform duration-300"
                    />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/elyumax"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={images.linkedin_gray}
                      alt="LinkedIn"
                      className="w-6 h-6 hover:scale-125 transition-transform duration-300"
                    />
                  </a>
                </div>
              </ul>
            )}
          </div>

          {/* Static Menu Items */}
          <a
            href="/our-process"
            className={`h-[80px] flex items-center  uppercase tracking-wide lg:text-base xl:text-lg 2xl:text-xl disabled ${
              styles.text
            } ${
              currentRoute === '/our-process'
                ? `${isScrolled ? 'border-b-4 border-red-500 pb-1' : 'border-b-4 border-white pb-1'}`
                : ''
            }${isScrolled ? 'hover:border-b-4 hover:border-red-500 pb-1' : 'hover:border-b-4 hover:border-white pb-1'}`}
          >
            {t('header.process')}
          </a>
          <a
            href="/contact-us"
            className={` h-[80px] flex items-center uppercase tracking-wide lg:text-base xl:text-lg 2xl:text-xl disabled ${
              styles.text
            } ${
              currentRoute === '/contact-us'
                ? `${isScrolled ? 'border-b-4 border-red-500 pb-1' : 'border-b-4 border-white pb-1'}`
                : ''
            }  ${isScrolled ? 'hover:border-b-4 hover:border-red-500 pb-1' : 'hover:border-b-4 hover:border-white pb-1'}`}
          >
            {t('header.contact')}
          </a>

          {/* Brochure Button */}
          <a
            href="/elyumax-brochure.pdf"
            target="_blank"
            rel="noopener noreferrer"
            id="shiny-red-button"
            className={` flex items-center flex uppercase lg:text-base xl:text-lg 2xl:text-xl tracking-wide color text-white py-3 px-4 2xl:py-4 2xl:px-5  ${styles.text} ${styles.btn}`}
          >
            {t('header.brochure')}
            <img
              src={images.downloadIcon}
              alt={'Download Icon'}
              className={`flex items-center  pl-1 2xl:pl-2 ${styles.download}`}
            />
          </a>
        </nav>
      </div>

      {/* Sidebar for Mobile and Tablet */}
      <div
        className={`fixed top-0 left-0 w-64 h-full bg-white text-black transition-transform duration-300 transform ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:hidden`}
      >
        <div className="p-6 flex flex-col h-full">
          <div className="flex justify-between pb-8">
            <img
              src={images.logo_white_bg}
              alt={'Logo'}
              className="w-32 h-12"
            />
            <button
              className="text-3xl focus:outline-none mb-6"
              onClick={toggleSidebar}
            >
              &times; {/* Close icon */}
            </button>
          </div>

          <ul
            className="space-y-6 flex-grow"
            style={{ fontFamily: "'Nunito Sans', sans-serif", fontWeight: 400 }}
          >
            {/* Company Dropdown inside Sidebar */}
            <li className="uppercase tracking-wide">
              <button
                onClick={toggleSidebarDropdown}
                className="flex uppercase items-center w-full text-left"
              >
                {t('header.company')}
                <svg
                  className="w-4 h-4 ml-1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isSidebarDropdownOpen && (
                <ul className=" mt-2 space-y-2 text-black">
                  <li>
                    <a
                      href="/about-us"
                      className={`block mt-5 ${
                        currentRoute === '/about-us'
                          ? 'text-red-500'
                          : 'text-black'
                      }`}
                      style={{
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#484848',
                      }}
                    >
                      {t('header.about')}
                    </a>
                  </li>
                  <li>
                    <a
                      href="/certifications"
                      className={`block disabled mt-5 ${
                        currentRoute === '/certifications'
                          ? 'text-red-500'
                          : 'text-black'
                      }`}
                      style={{
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#484848',
                      }}
                    >
                      {t('header.certificate')}
                    </a>
                  </li>
                  <li>
                    <a
                      href="/careers"
                      className={`block disabled mt-5 ${
                        currentRoute === '/careers'
                          ? 'text-red-500'
                          : 'text-black'
                      }`}
                      style={{
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#484848',
                      }}
                    >
                      {t('header.careers')}
                    </a>
                  </li>
                </ul>
              )}
            </li>

            <div className="flex justify-center space-x-3 px-1 w-10 border-t border-red-500"></div>

            <li className="uppercase tracking-wide">
              <a href="/our-process">{t('header.process')}</a>
            </li>
            <div className="flex justify-center space-x-3 px-1 w-10 border-t border-red-500"></div>
            <li className="uppercase tracking-wide">
              <a href="/contact-us">{t('header.contact')}</a>
            </li>
            <div className="flex justify-center space-x-3 px-1 w-10  border-t border-red-500"></div>

            <a
              href="/elyumax-brochure.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="uppercase tracking-wide color text-white py-2 px-4 hover:bg-red-500 transition"
            >
              {t('header.brochure')}
            </a>
          </ul>

          {/* Social Icons */}
          <div className="flex-none pt-4">
            <div className="flex space-x-5 py-2">
              <a
                href="https://www.facebook.com/elyumax"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={images.facebook_black} alt="Facebook" />
              </a>
              <a
                href="https://www.instagram.com/elyumax"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={images.instagram_black} alt="Instagram" />
              </a>
              <a
                href="https://x.com/elyumax"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={images.twitter_black} alt="Twitter" />
              </a>
              <a
                href="https://www.linkedin.com/company/elyumax"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={images.linkedin_black} alt="LinkedIn" />
              </a>
            </div>
            <span className="block py-2">Copyright © 2024 Elyumax </span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
